import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "bookModal", "collectionModal", "bookSearch", "collectionSearch",
    "bookResults", "collectionResults", "bookInput", "collectionInput",
    "bookPill", "collectionPill", "bookName", "collectionName", "attachedItems"
  ]
  static values = {
    bookItems: Array,
    collectionItems: Array
  }

  // Modal functionality
  openBookModal() {
    this.bookModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    setTimeout(() => this.bookSearchTarget.focus(), 100)
  }

  openCollectionModal() {
    this.collectionModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    setTimeout(() => this.collectionSearchTarget.focus(), 100)
  }

  closeModals() {
    this.bookModalTarget.classList.add("hidden")
    this.collectionModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // Search functionality
  searchBooks() {
    const query = this.bookSearchTarget.value.trim().toLowerCase()
    if (query.length === 0) {
      this.bookResultsTarget.innerHTML = `<p class="text-sm text-beige-500 text-center py-4">Start typing to search your books</p>`
      return
    }

    const filtered = this.bookItemsValue.filter(item =>
      item.label.toLowerCase().includes(query)
    ).slice(0, 10)

    this.renderBookResults(filtered)
  }

  searchCollections() {
    const query = this.collectionSearchTarget.value.trim().toLowerCase()
    if (query.length === 0) {
      this.collectionResultsTarget.innerHTML = `<p class="text-sm text-beige-500 text-center py-4">Start typing to search your collections</p>`
      return
    }

    const filtered = this.collectionItemsValue.filter(item =>
      item.label.toLowerCase().includes(query)
    ).slice(0, 10)

    this.renderCollectionResults(filtered)
  }

  renderBookResults(items) {
    if (items.length === 0) {
      this.bookResultsTarget.innerHTML = `<p class="text-sm text-beige-500 text-center py-4">No books found</p>`
      return
    }

    this.bookResultsTarget.innerHTML = items.map(item => `
      <button type="button"
              class="w-full text-left p-3 rounded-lg hover:bg-beige-50 transition-colors flex items-center gap-3"
              data-action="click->post-composer#selectBook"
              data-id="${item.id}"
              data-label="${this.escapeHtml(item.label)}">
        ${item.image ? `<img src="${item.image}" class="w-10 h-14 object-cover rounded shadow-sm shrink-0" alt="">` : `<div class="w-10 h-14 bg-beige-100 rounded flex items-center justify-center text-beige-400 text-xs">No img</div>`}
        <div class="min-w-0 flex-1">
          <p class="font-medium text-beige-900 truncate">${this.escapeHtml(item.label)}</p>
          ${item.detail ? `<p class="text-xs text-beige-500 truncate">${this.escapeHtml(item.detail)}</p>` : ''}
        </div>
      </button>
    `).join('')
  }

  renderCollectionResults(items) {
    if (items.length === 0) {
      this.collectionResultsTarget.innerHTML = `<p class="text-sm text-beige-500 text-center py-4">No collections found</p>`
      return
    }

    this.collectionResultsTarget.innerHTML = items.map(item => `
      <button type="button"
              class="w-full text-left p-3 rounded-lg hover:bg-beige-50 transition-colors flex items-center gap-3"
              data-action="click->post-composer#selectCollection"
              data-id="${item.id}"
              data-label="${this.escapeHtml(item.label)}">
        <div class="w-10 h-10 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-center text-amber-700">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
        </div>
        <div class="min-w-0 flex-1">
          <p class="font-medium text-beige-900 truncate">${this.escapeHtml(item.label)}</p>
        </div>
      </button>
    `).join('')
  }

  selectBook(event) {
    const button = event.currentTarget
    const id = button.dataset.id
    const label = button.dataset.label

    this.bookInputTarget.value = id
    this.bookNameTarget.textContent = label
    this.bookPillTarget.classList.remove("hidden")
    this.updateAttachedItemsVisibility()
    this.closeModals()
    this.bookSearchTarget.value = ""
  }

  selectCollection(event) {
    const button = event.currentTarget
    const id = button.dataset.id
    const label = button.dataset.label

    this.collectionInputTarget.value = id
    this.collectionNameTarget.textContent = label
    this.collectionPillTarget.classList.remove("hidden")
    this.updateAttachedItemsVisibility()
    this.closeModals()
    this.collectionSearchTarget.value = ""
  }

  removeBook() {
    this.bookInputTarget.value = ""
    this.bookPillTarget.classList.add("hidden")
    this.updateAttachedItemsVisibility()
  }

  removeCollection() {
    this.collectionInputTarget.value = ""
    this.collectionPillTarget.classList.add("hidden")
    this.updateAttachedItemsVisibility()
  }

  updateAttachedItemsVisibility() {
    const hasBook = this.bookInputTarget.value !== ""
    const hasCollection = this.collectionInputTarget.value !== ""

    if (hasBook || hasCollection) {
      this.attachedItemsTarget.classList.remove("hidden")
    } else {
      this.attachedItemsTarget.classList.add("hidden")
    }
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
